import { useId, useState } from 'react';
import { SearchIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useCurrentUser } from '@/features/identity';
import {
  workspaceMarkWords,
  workspaceMarks,
} from '@/features/workspaces/marks';
import type { WorkspaceSummary } from '@/lib/api/generated/model';
import { usePreferencesStore } from '@/shared/store/preferences';

/**
 * Where a copy goes: a search over the person's workspaces, in the dialog's own
 * body (dialogs review 2, slice 3).
 *
 * It used to be a dropdown, which with thirty workspaces covered the dialog and
 * its footer and offered no way to find one. Choosing the target is the copy's
 * one decision, so the list is the body until it is made: the workspaces opened
 * last on this device first, as the front door orders them, then the rest by
 * name, each with the marks the workspace list shows (Private, Owner). Chosen,
 * it folds to one line with Change, and the review takes the room below.
 */
export function WorkspaceTargetPicker({
  targets,
  value,
  onChange,
  disabled = false,
}: {
  targets: readonly WorkspaceSummary[];
  value: string | null;
  onChange: (id: string | null) => void;
  disabled?: boolean;
}) {
  const [query, setQuery] = useState('');
  const recent = usePreferencesStore((state) => state.recentWorkspaces);
  const me = useCurrentUser();
  const searchId = useId();
  const chosen = targets.find((workspace) => workspace.id === value);

  if (chosen) {
    return (
      <div className="border-border flex min-h-11 items-center gap-2 rounded-md border px-3 py-1.5 text-sm">
        <p className="min-w-0 flex-1 truncate">
          Into <span className="font-medium">{chosen.name}</span>
        </p>
        <Button
          variant="ghost"
          size="sm"
          disabled={disabled}
          aria-label={`Change where to copy (now ${chosen.name})`}
          onClick={() => {
            setQuery('');
            onChange(null);
          }}
        >
          Change
        </Button>
      </div>
    );
  }

  const order = new Map(recent.map((entry, index) => [entry.id, index]));
  const needle = query.trim().toLocaleLowerCase();
  const shown = targets
    .filter((workspace) =>
      needle === ''
        ? true
        : workspace.name.toLocaleLowerCase().includes(needle),
    )
    .sort((a, b) => {
      const ra = order.get(a.id) ?? Infinity;
      const rb = order.get(b.id) ?? Infinity;
      return ra !== rb ? ra - rb : a.name.localeCompare(b.name);
    });

  return (
    <div className="flex min-h-0 flex-col gap-2">
      <label htmlFor={searchId} className="text-sm font-medium">
        Copy into
      </label>
      <div className="relative">
        <SearchIcon
          aria-hidden="true"
          strokeWidth={1.5}
          className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
        />
        <Input
          id={searchId}
          type="search"
          value={query}
          placeholder="Find a workspace"
          autoFocus
          disabled={disabled}
          className="ps-8"
          onChange={(event) => {
            setQuery(event.target.value);
          }}
          onKeyDown={(event) => {
            // The one match a search narrows to is the one meant.
            if (event.key === 'Enter' && shown[0]) {
              event.preventDefault();
              onChange(shown[0].id);
            }
          }}
        />
      </div>
      {shown.length === 0 ? (
        <p className="text-muted-foreground px-1 text-sm">
          No workspace matches “{query.trim()}”.
        </p>
      ) : (
        <ul
          aria-label="Workspaces"
          className="border-border flex max-h-56 flex-col overflow-y-auto rounded-md border p-1"
        >
          {shown.map((workspace) => {
            const marks = workspaceMarkWords(
              workspaceMarks(workspace, me.data?.id),
            );
            return (
              <li key={workspace.id}>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onChange(workspace.id)}
                  className="hover:bg-muted/50 focus-visible:ring-ring touch-target flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm focus-visible:ring-3 focus-visible:outline-none"
                >
                  <span className="min-w-0 flex-1 truncate">
                    {workspace.name}
                  </span>
                  {marks.length > 0 && (
                    <span className="text-muted-foreground shrink-0 text-xs">
                      {marks.join(' · ')}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
